import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';

const ControlsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  justify-content: center;
`;

const ScoreButton = styled.button`
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.button};
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.small};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
  
  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: ${({ theme }) => theme.shadows.small};
  }
  
  &:disabled {
    background: ${({ theme }) => theme.colors.disabledBackground};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const TeamAButton = styled(ScoreButton)`
  background: ${({ theme }) => theme.colors.teal};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const TeamBButton = styled(ScoreButton)`
  background: ${({ theme }) => theme.colors.orange};
  
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const HotkeyHint = styled.span`
  position: absolute;
  top: -1.2rem;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.darkBackground};
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  white-space: nowrap;
  opacity: 0.8;
`;

export default function ScoreControls({ onUpdateScore, disabled }) {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const isPrivileged = roles.includes('ADMIN') || roles.includes('SITE_OWNER');

  useEffect(() => {
    if (!isPrivileged || disabled) return;

    const handleKeyPress = (e) => {
      // Only trigger if not typing in an input or textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key.toLowerCase() === 'q') {
        onUpdateScore('teamA');
      } else if (e.key.toLowerCase() === 'p') {
        onUpdateScore('teamB');
      }
    };

    window.addEventListener('keyup', handleKeyPress);
    return () => window.removeEventListener('keyup', handleKeyPress);
  }, [isPrivileged, onUpdateScore, disabled]);

  if (!isPrivileged || disabled) return null;

  return (
    <ControlsWrapper>
      <TeamAButton onClick={() => onUpdateScore('teamA')}>
        <HotkeyHint>Press Q</HotkeyHint>
        +1 Team A
      </TeamAButton>
      <TeamBButton onClick={() => onUpdateScore('teamB')}>
        <HotkeyHint>Press P</HotkeyHint>
        +1 Team B
      </TeamBButton>
    </ControlsWrapper>
  );
}

ScoreControls.propTypes = {
  onUpdateScore: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

ScoreControls.defaultProps = {
  disabled: false
}; 